<template>
    <tr>
        <td class="text-center">
            <span class="ix-drag-icon handle-line"></span>
        </td>
        <td class="text-center">
            <input type="checkbox" value="1" :id="'is_title_' + deliveryLine.uniq" :name="'delivery_lines[' + index + '][is_title]'" v-model="deliveryLine.is_title" @change="compute" :disabled="deliveryLine.is_eco_participation">
        </td>
        <td>
            <input type="text" class="form-control form-control-sm" :name="'delivery_lines[' + index + '][reference]'" v-model="deliveryLine.reference" :readonly="deliveryLine.is_eco_participation">
        </td>
        <td>
            <div class="d-flex align-items-center">
                <!-- Un kit se déplie pour voir les quantités de composants livrées. -->
                <button type="button" class="ix-kit-toggle mr-1" :class="{'is-open': deliveryLine.kit_open}"
                        v-if="deliveryLine.kit_components && deliveryLine.kit_components.length > 0"
                        :title="'Article composé · ' + deliveryLine.kit_components.length + (deliveryLine.kit_components.length > 1 ? ' composants' : ' composant')"
                        @click="deliveryLine.kit_open = !deliveryLine.kit_open">
                    <i class="far fa-cubes"></i><i class="far fa-angle-down ml-1"></i>
                </button>
                <input placeholder="Commencer la saisie pour obtenir des suggestions .." class="form-control form-control-sm" :id="'suggestionAutocomplete' + deliveryLine.uniq" type="text" :name="'delivery_lines[' + index + '][designation]'" v-model="deliveryLine.designation" @input="emitEventLineEdited" :readonly="deliveryLine.is_eco_participation">
            </div>

            <table class="ix-table ix-table-sm mt-2" v-if="deliveryLine.kit_open && deliveryLine.kit_components && deliveryLine.kit_components.length > 0">
                <thead>
                <tr>
                    <th>Référence</th>
                    <th>Désignation</th>
                    <th class="text-right">Qté / kit</th>
                    <th>Unité</th>
                    <th class="text-right">Qté totale</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(component, componentIndex) in deliveryLine.kit_components" :key="'kit_' + componentIndex">
                    <td>{{ component.number || '-' }}</td>
                    <td>{{ component.name }}</td>
                    <td class="text-right">{{ formatQuantity(component.quantity) }}</td>
                    <td>{{ component.unit || 'U' }}</td>
                    <td class="text-right">{{ formatQuantity(component.quantity * (parseFloat(deliveryLine.quantity) || 0)) }}</td>
                </tr>
                </tbody>
            </table>
            <input type="hidden" :name="'delivery_lines[' + index + '][id]'" v-model="deliveryLine.id">
            <input type="hidden" :name="'delivery_lines[' + index + '][line_id]'" v-model="deliveryLine.line_id">

            <div v-show="is_expanded === true">
                <textarea rows="2" class="form-control form-control-sm mt-2" placeholder="description, informations complémentaires, .." :name="'delivery_lines[' + index + '][description]'" v-model="deliveryLine.description"></textarea>
            </div>

            <div v-if="deliveryLine.has_serial_numbers && deliveryLine.serial_numbers && deliveryLine.serial_numbers.length > 0" style="margin-top: 8px;">
                <div style="font-size: 12px; color: #586069; margin-bottom: 4px;"><i class="far fa-barcode" style="margin-right: 4px;"></i>Numéros de série sélectionnés ({{ deliveryLine.serial_numbers.length }})</div>
                <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                    <span v-for="(sn, snIndex) in deliveryLine.serial_numbers" :key="snIndex" style="display: inline-flex; align-items: center; background: #e8f5e9; border: 1px solid #c8e6c9; border-radius: 4px; padding: 2px 8px; font-size: 12px; color: #2e7d32;">
                        <i class="far fa-barcode" style="margin-right: 4px;"></i>{{ sn }}
                        <i class="far fa-times" style="margin-left: 6px; cursor: pointer;" @click="removeSerialNumber(snIndex)"></i>
                    </span>
                </div>
                <input type="hidden" v-for="(sn, snIndex) in deliveryLine.serial_numbers" :key="'sn_' + snIndex" :name="'delivery_lines[' + index + '][serial_numbers][' + snIndex + '][serial_number]'" :value="sn">
            </div>

            <!-- Ligne de kit : les numéros de chaque composant à numéros, postés avec leur composant. -->
            <template v-if="kitSerialComponents.length > 0">
                <kit-serial-numbers-input v-for="component in kitSerialComponents" :key="'kit_sn_' + component.article_id"
                                          :component="component" :kits="deliveryLine.quantity" :values="kitSerialsOf(component.article_id)"
                                          :taken="serialsTakenElsewhere(component.article_id, deliveryLine)"
                                          @add="addKitSerial(component.article_id, $event)" @remove="removeKitSerial(component.article_id, $event)">
                </kit-serial-numbers-input>
                <input type="hidden" v-for="(sn, snIndex) in deliveryLine.serial_numbers" :key="'kit_sn_value_' + snIndex" :name="'delivery_lines[' + index + '][serial_numbers][' + snIndex + '][serial_number]'" :value="sn.serial_number">
                <input type="hidden" v-for="(sn, snIndex) in deliveryLine.serial_numbers" :key="'kit_sn_article_' + snIndex" :name="'delivery_lines[' + index + '][serial_numbers][' + snIndex + '][article_id]'" :value="sn.article_id">
            </template>

            <div v-if="deliveryLine.has_serial_numbers && parseInt(deliveryLine.quantity) > 0 && deliveryLine.serial_numbers && deliveryLine.serial_numbers.length < parseInt(deliveryLine.quantity)" style="margin-top: 6px; font-size: 12px; color: #d15240;">
                <i class="far fa-exclamation-triangle" style="margin-right: 4px;"></i>
                {{ parseInt(deliveryLine.quantity) - (deliveryLine.serial_numbers ? deliveryLine.serial_numbers.length : 0) }} numéro(s) de série manquant(s) sur {{ parseInt(deliveryLine.quantity) }} attendu(s)
            </div>

            <div v-if="lineErrors && lineErrors.length > 0" style="margin-top: 6px; font-size: 12px; color: #d15240;">
                <div v-for="(err, errIndex) in lineErrors" :key="'err_' + errIndex">
                    <i class="far fa-exclamation-circle" style="margin-right: 4px;"></i>{{ err }}
                </div>
            </div>
        </td>
        <td v-if="deliveryLine.is_title == 1"></td>

        <td v-if="deliveryLine.is_title != 1">
            <input type="number" class="form-control form-control-sm" :name="'delivery_lines[' + index + '][quantity]'" v-model="deliveryLine.quantity" required step="0.001" :readonly="deliveryLine.is_eco_participation">
            <input type="text" class="form-control form-control-sm mt-2" placeholder="unité" :name="'delivery_lines[' + index + '][unit]'" v-model="deliveryLine.unit" v-show="is_expanded === true">
        </td>
        <td class="text-right">
            <span class="btn btn-sm transparent" @click="expandRow">
                <i class="far fa-chevron-up" v-if="is_expanded === true"></i>
                <i class="far fa-chevron-down" v-if="is_expanded === false"></i>
            </span>

            <span class="btn btn-sm transparent" @click="emitEventLineDeleted">
                <i class="far fa-trash"></i>
            </span>
        </td>
    </tr>
</template>

<script>
    import $ from 'jquery'
    import Swal from "sweetalert2";
    import KitSerialNumbersInput from "./KitSerialNumbersInput";

    export default {
        name: 'delivery-line-form',
        props: ['deliveryLine', 'index', 'suggestionsRoute', 'lineErrors'],
        inject: {
            serialsTakenElsewhere: { default: () => () => [] }
        },
        components: {
            KitSerialNumbersInput
        },
        data() {
            return {
                errors: [],
                is_expanded: false,
                suggestions: []
            };
        },
        computed: {
            kitSerialComponents: function () {
                return (this.deliveryLine.kit_components || []).filter(function (component) {
                    return component.has_serial_numbers;
                });
            }
        },
        methods : {
            kitSerialsOf: function (articleId) {
                return this.deliveryLine.serial_numbers.filter(function (sn) {
                    return parseInt(sn.article_id) === articleId;
                }).map(function (sn) {
                    return sn.serial_number;
                });
            },
            addKitSerial: function (articleId, value) {
                this.deliveryLine.serial_numbers.push({serial_number: value, article_id: articleId});
            },
            /** Retire le n-ième numéro du composant, dans la liste de tous les composants. */
            removeKitSerial: function (articleId, componentIndex) {
                var seen = -1;
                var position = this.deliveryLine.serial_numbers.findIndex(function (sn) {
                    if (parseInt(sn.article_id) === articleId) seen++;
                    return seen === componentIndex;
                });
                if (position !== -1) {
                    this.deliveryLine.serial_numbers.splice(position, 1);
                }
            },
            /** Une quantité sans décimales inutiles : « 12 » et non « 12,000 ». */
            formatQuantity: function(value) {
                return (parseFloat(value) || 0).toFixed(3).replace(/\.?0+$/, '').replace('.', ',');
            },
            expandRow: function() {
                this.is_expanded = !this.is_expanded;
            },
            compute: function() {
                // Prévention champs vide
                if (this.deliveryLine.quantity == '' || this.deliveryLine.quantity == null) { this.deliveryLine.quantity = 0 }

                if (this.deliveryLine.is_title) {
                    this.deliveryLine.quantity = 1;
                    this.deliveryLine.unit = null;
                }
            },
            emitEventLineEdited: function () {
                this.$emit('line-editing-started', this.index)
            },
            removeSerialNumber: function(index) {
                this.deliveryLine.serial_numbers.splice(index, 1);
            },
            emitEventLineDeleted: function () {
                Swal.fire({
                    title: 'Êtes-vous sûr ?',
                    text: "",
                    icon: 'warning',
                    showCancelButton: true,
                    cancelButtonText: 'Annuler',
                    confirmButtonColor: '#D15240',
                    confirmButtonText: 'Oui, supprimer',
                    reverseButtons: true,
                    heightAuto: false
                }).then((result) => {
                    if (result.value) {
                        this.$emit('line-deleted', this.index)
                    }
                })
            }
        },
        mounted() {
            // Expand si description
            if (this.deliveryLine.description) {
                this.is_expanded = true;
            }

            this.compute()

            // Creation autocomplete
            var vm = this;
            $("#suggestionAutocomplete" + this.deliveryLine.uniq).autocomplete({
                minLength: 0,
                source: (request, response) => {
                    $.ajax({
                        url : this.suggestionsRoute,
                        type: 'get',
                        dataType : 'json',
                        headers: this.getHttpJsonRequestHeaders().headers,
                        data: {...{ q: request.term }, ...{order_id: $('#order_id').val()}},
                        success: (data) => {
                            response($.map(data, (item) => {
                                return Object.assign(item, {
                                    label: item.designation,
                                    value: item.designation,
                                })
                            }))
                        }
                    });
                },
                create: function () {
                    $(this).data('ui-autocomplete')._renderItem = function (ul, item) {
                        var html = `<div class="d-flex align-items-center">
                                      <span>${item.autocomplete_label}</span>`;

                        if (item.is_title) {
                            html += `</div>`
                        } else {
                            html += `<span class="ml-auto">Qté : ${item.quantity}</span>
                                    </div>`
                        }

                        var $li = $('<li>').append(html);

                        if (item.has_serial_numbers && item.available_serial_numbers && item.available_serial_numbers.length > 0) {
                            var availableSNs = item.available_serial_numbers.filter(function(sn) {
                                return !vm.deliveryLine.serial_numbers.includes(sn);
                            });
                            if (availableSNs.length === 0) {
                                $li.append('<div style="font-size: 11px; color: #d15240; padding-top: 2px;"><i class="far fa-barcode" style="margin-right: 4px;"></i>Tous les numéros de série ont été sélectionnés</div>');
                            } else {
                                availableSNs.forEach(function(sn) {
                                    var $snLine = $('<div>')
                                        .css({
                                            'font-size': '12px',
                                            'padding': '4px 8px',
                                            'cursor': 'pointer',
                                            'color': '#24292e',
                                            'border-top': '1px solid #eaecef'
                                        })
                                        .html('<i class="far fa-barcode" style="margin-right: 6px; color: #586069;"></i>' + sn)
                                        .on('mouseenter', function() { $(this).css('background-color', '#f6f8fa'); })
                                        .on('mouseleave', function() { $(this).css('background-color', 'transparent'); })
                                        .on('mousedown', function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        })
                                        .on('click', function(e) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (vm.deliveryLine.line_id !== item.id) {
                                                vm.deliveryLine.reference = item.reference;
                                                vm.deliveryLine.designation = item.designation;
                                                vm.deliveryLine.quantity = item.quantity;
                                                vm.deliveryLine.unit = item.unit;
                                                vm.deliveryLine.description = item.description;
                                                vm.deliveryLine.is_title = item.is_title;
                                                vm.deliveryLine.line_id = item.id;
                                                vm.deliveryLine.has_serial_numbers = item.has_serial_numbers || false;
                                                vm.deliveryLine.available_serial_numbers = item.available_serial_numbers || [];
                                                vm.deliveryLine.serial_numbers = [];
                                            }
                                            if (!vm.deliveryLine.serial_numbers.includes(sn)) {
                                                vm.deliveryLine.serial_numbers.push(sn);
                                            }
                                            $(this).css('color', '#1fa065').html('<i class="far fa-check" style="margin-right: 6px;"></i>' + sn);
                                            var $input = $("#suggestionAutocomplete" + vm.deliveryLine.uniq);
                                            var currentTerm = $input.val();
                                            setTimeout(function() {
                                                $input.autocomplete("search", currentTerm);
                                            }, 50);
                                        });
                                    $li.append($snLine);
                                });
                            }
                        } else if (item.has_serial_numbers) {
                            $li.append('<div style="font-size: 11px; color: #d15240; padding-top: 2px;"><i class="far fa-barcode" style="margin-right: 4px;"></i>Aucun numéro de série disponible</div>');
                        }

                        return $li.appendTo(ul);
                    };
                },
                change: (e, item) => {
                    if(!item.item && !this.deliveryLine.line_id){
                        this.deliveryLine.line_id = null
                        this.deliveryLine.has_serial_numbers = false
                        this.deliveryLine.available_serial_numbers = []
                        this.deliveryLine.serial_numbers = []
                    }
                },
                select: (e, item) => {
                    var previousLineId = this.deliveryLine.line_id
                    this.deliveryLine.reference = item.item.reference
                    this.deliveryLine.designation = item.item.designation
                    this.deliveryLine.quantity = item.item.quantity
                    this.deliveryLine.unit = item.item.unit
                    this.deliveryLine.description = item.item.description
                    this.deliveryLine.is_title = item.item.is_title
                    this.deliveryLine.line_id = item.item.id
                    this.deliveryLine.has_serial_numbers = item.item.has_serial_numbers || false
                    this.deliveryLine.available_serial_numbers = item.item.available_serial_numbers || []
                    this.deliveryLine.kit_components = item.item.kit_components || []
                    this.deliveryLine.kit_open = false
                    // Une autre ligne de commande : les numéros saisis ne la concernent plus.
                    if (previousLineId !== item.item.id) {
                        this.deliveryLine.serial_numbers = []
                    }
                }
            }).focus(() => {
                $("#suggestionAutocomplete" + this.deliveryLine.uniq).autocomplete("search", '');
            });
        }
    }
</script>
